import type { ChartOptions, TooltipItem } from 'chart.js';
import type { ChartPayload } from '@oxxean/types';
import { formatNumber } from './format';

/**
 * Colores y opciones de los gráficos, derivados de los tokens CSS.
 *
 * Se leen del DOM en lugar de duplicarse en TypeScript: así el modo oscuro cambia
 * los ejes y las series sin mantener dos paletas en dos lenguajes distintos.
 */
export interface ChartTheme {
  series: string[];
  grid: string;
  tick: string;
  text: string;
  tooltipBg: string;
  /** Fondo de la tarjeta: halo detrás de las etiquetas de datos. */
  surface: string;
}

// Paleta categórica dedicada (tokens `--series-N`), validada para daltonismo y
// contraste. NO se usan los tokens semánticos (--red/--green son estados).
const SERIES_TOKENS = ['--series-1', '--series-2', '--series-3', '--series-4', '--series-5', '--series-6'] as const;
const SERIES_FALLBACK = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300'];

function cssVar(styles: CSSStyleDeclaration, name: string, fallback: string): string {
  const value = styles.getPropertyValue(name).trim();
  return value.length > 0 ? value : fallback;
}

export function readChartTheme(): ChartTheme {
  if (typeof window === 'undefined') {
    return {
      series: SERIES_FALLBACK,
      grid: '#E2E9F0',
      tick: '#6B7B8B',
      text: '#1A2A38',
      tooltipBg: '#0B2E4F',
      surface: '#FFFFFF',
    };
  }
  const styles = window.getComputedStyle(document.documentElement);
  return {
    series: SERIES_TOKENS.map((token, index) => cssVar(styles, token, SERIES_FALLBACK[index] ?? '#2a78d6')),
    grid: cssVar(styles, '--line', '#E2E9F0'),
    tick: cssVar(styles, '--muted', '#6B7B8B'),
    text: cssVar(styles, '--ink', '#1A2A38'),
    tooltipBg: cssVar(styles, '--navy', '#0B2E4F'),
    surface: cssVar(styles, '--card', '#FFFFFF'),
  };
}

/** Convierte `#RRGGBB` en `rgba()` para rellenos translúcidos. */
export function withAlpha(color: string, alpha: number): string {
  const hex = color.replace('#', '');
  if (hex.length !== 6) return color;
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Formato del valor en tooltips y ejes.
 *
 * Se infiere del título del gráfico, no de un cálculo: los porcentajes ya llegan
 * como porcentaje desde el backend, y el frontend sólo decide cuántos decimales
 * muestra.
 */
export function inferChartFormat(payload: ChartPayload): 'integer' | 'percent' {
  return /%|porcenta|tasa|cumplimiento/i.test(payload.title) ? 'percent' : 'integer';
}

/**
 * Las opciones se declaran con la forma de `bar` porque es el superconjunto que
 * necesita esta aplicación (ejes + leyenda + tooltip); el componente las adapta
 * al tipo concreto al renderizar.
 */
export function buildChartOptions(payload: ChartPayload, theme: ChartTheme): ChartOptions<'bar'> {
  const valueFormat = inferChartFormat(payload);
  const horizontal = payload.type === 'horizontalBar';
  const isDoughnut = payload.type === 'doughnut';
  const showLegend = payload.series.length > 1 || isDoughnut;
  /** Cuántas porciones tiene el anillo: decide dónde cabe la leyenda. */
  const categoriasDelAnillo = isDoughnut ? payload.labels.length : 0;

  const label = (item: TooltipItem<'bar'>): string => {
    const raw = item.raw;
    const prefix = item.dataset.label ?? item.label;
    if (typeof raw !== 'number') {
      // Un punto nulo NO se dibuja como cero: se declara sin registro.
      return `${prefix}: sin registros`;
    }

    const cifra = formatNumber(raw, valueFormat);

    // En un gráfico de anillo se añade la PARTICIPACIÓN sobre el total.
    //
    // Es la pregunta que se hace quien mira un anillo —«cuánto de todo es esto»— y
    // sin el porcentaje hay que calcularlo mentalmente contra los demás segmentos.
    // Se computa sobre los valores numéricos del conjunto, saltando los nulos: un
    // punto sin registro no participa de un total que no lo incluye.
    if (isDoughnut) {
      const valores = (item.dataset.data as Array<number | null>).filter(
        (v): v is number => typeof v === 'number',
      );
      const total = valores.reduce((suma, v) => suma + v, 0);
      if (total > 0) {
        const parte = (raw / total) * 100;
        return `${prefix}: ${cifra} (${formatNumber(parte, 'decimal')} %)`;
      }
    }

    return `${prefix}: ${cifra}`;
  };

  /**
   * Encabezado del recuadro: el punto del eje de categorías.
   *
   * Chart.js lo omite en los anillos, donde el título queda vacío y el recuadro
   * aparece sin contexto. Aquí se fuerza a que siempre diga a QUÉ pertenece el
   * valor: la empresa, el área o el mes.
   */
  const title = (items: TooltipItem<'bar'>[]): string => {
    const primero = items[0];
    if (!primero) return '';
    return isDoughnut ? (primero.label ?? '') : (primero.label ?? '');
  };

  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',

    /**
     * Interacción del puntero.
     *
     * ── Por qué el modo depende del tipo ──────────────────────────────────────
     * `index` agrupa todas las series de una misma categoría, que es lo correcto en
     * barras y líneas: al pasar por «Junio» conviene ver las series a la vez.
     *
     * Pero en un ANILLO no hay eje de categorías, y con `index` el recuadro solo
     * aparecía justo encima del arco: moverse un píxel hacia el centro o el borde lo
     * hacía desaparecer. Por eso los anillos usan `nearest`, que responde al
     * segmento más próximo al puntero.
     *
     * `intersect: false` es lo que permite que el recuadro aparezca sin acertar
     * exactamente encima del trazo, que en una línea de un píxel es casi imposible.
     */
    interaction: isDoughnut
      ? { mode: 'nearest', intersect: true }
      : { mode: 'index', intersect: false },

    // Se declara explícitamente que el puntero reacciona en toda el área del
    // gráfico, no solo sobre los elementos dibujados.
    hover: { mode: isDoughnut ? 'nearest' : 'index', intersect: isDoughnut },
    plugins: {
      legend: {
        display: showLegend,
        /**
         * La leyenda del anillo va a la DERECHA solo si hay pocas categorías.
         *
         * Con muchas —«Dotación por área» tiene 31— la columna derecha se parte en
         * dos y las etiquetas quedan cortadas: «Contabilidad y…», «Gestión de Per…».
         * Y son justo las categorías pequeñas, las que no caben sobre su porción y
         * dependen de la leyenda para poder leerse.
         *
         * Abajo dispone del ancho completo de la tarjeta y las etiquetas caben
         * enteras. El anillo queda algo más pequeño, que es un intercambio a favor:
         * un anillo grande con etiquetas ilegibles no informa.
         */
        position: isDoughnut ? (categoriasDelAnillo > 8 ? 'bottom' : 'right') : 'top',
        align: isDoughnut && categoriasDelAnillo > 8 ? 'start' : 'end',
        labels: {
          color: theme.tick,
          boxWidth: 10,
          boxHeight: 10,
          usePointStyle: true,
          padding: isDoughnut ? 7 : 12,
          /**
           * En un anillo, la leyenda lleva el VALOR y el porcentaje.
           *
           * Sobre las porciones solo caben las grandes: con 31 áreas, escribir un
           * porcentaje sobre una porción de dos grados produce texto encimado. La
           * leyenda es donde las pequeñas se pueden leer, y una leyenda que solo
           * repite el nombre de la categoría no aporta nada que no diga el color.
           */
          generateLabels: isDoughnut
            ? (chart) => {
                const datos = chart.data.datasets[0]?.data ?? [];
                const total = (datos as Array<number | null>).reduce<number>(
                  (suma, v) => suma + (typeof v === 'number' ? v : 0),
                  0,
                );
                const colores = chart.data.datasets[0]?.backgroundColor;

                return (chart.data.labels ?? []).map((etiqueta, indice) => {
                  const valor = datos[indice];
                  const numero = typeof valor === 'number' ? valor : null;

                  // Un punto nulo se declara sin registro, NO como cero: es la misma
                  // regla del recuadro y del resto de la plataforma.
                  const sufijo =
                    numero === null
                      ? ': sin registros'
                      : total > 0
                        ? `: ${formatNumber(numero, 'integer')} (${formatNumber((numero / total) * 100, 'decimal')} %)`
                        : `: ${formatNumber(numero, 'integer')}`;

                  return {
                    text: `${String(etiqueta)}${sufijo}`,
                    fillStyle: Array.isArray(colores) ? (colores[indice] as string) : undefined,
                    strokeStyle: 'transparent',
                    lineWidth: 0,
                    // `hidden` se LEE del gráfico, no se fija en `false`.
                    //
                    // Fijarlo rompía el clic en la leyenda: Chart.js oculta la porción
                    // pero la entrada seguía dibujándose como activa, sin el tachado,
                    // de modo que no había forma de saber qué se había ocultado ni de
                    // recordar cómo volver.
                    hidden: !chart.getDataVisibility(indice),
                    index: indice,
                    pointStyle: 'circle' as const,
                  };
                });
              }
            : undefined,
        },
      },
      tooltip: {
        // Explícito: el recuadro al pasar el puntero es la única forma de leer la
        // cifra exacta de un gráfico, y debe estar en TODOS.
        enabled: true,
        backgroundColor: theme.tooltipBg,
        padding: 10,
        cornerRadius: 6,
        displayColors: true,
        titleFont: { weight: 'bold' },
        // Sin esto, el recuadro se recorta dentro del lienzo en los gráficos
        // pequeños y las etiquetas largas quedan cortadas.
        caretPadding: 6,
        callbacks: { title, label },
      },
    },
    scales: isDoughnut ? {} : buildScales({ horizontal, theme, valueFormat }),
  };
}

/**
 * Escalas del gráfico, distinguiendo el eje de VALOR del de CATEGORÍA.
 *
 * ── El defecto que corrige ─────────────────────────────────────────────────
 * `beginAtZero` estaba fijo en el eje `y`. Con `indexAxis: 'y'` —las barras
 * horizontales de «Dotación por empresa»— ese eje es el de CATEGORÍAS, y forzarlo
 * a comportamiento numérico hacía que Chart.js dibujara los índices `0, 1, 2, 3, 4`
 * en lugar de «POXX, PCH, Tungulú, UCO, Patagonia».
 *
 * El gráfico no fallaba ni avisaba: mostraba barras correctas junto a etiquetas
 * inservibles, y quien lo mirara no podía saber qué empresa era cada barra.
 *
 * `beginAtZero` y el formateador de cifras pertenecen al eje de valor, que es `x`
 * cuando el gráfico es horizontal y `y` cuando es vertical. Se calcula una vez y
 * se aplica al eje correcto, en lugar de repetir la condición en cada propiedad.
 */
function buildScales(input: {
  horizontal: boolean;
  theme: ChartTheme;
  valueFormat: ReturnType<typeof inferChartFormat>;
}): NonNullable<ChartOptions<'bar'>['scales']> {
  const { horizontal, theme, valueFormat } = input;

  const valueAxis = {
    // La rejilla acompaña al eje de valor: es la que ayuda a leer la magnitud.
    grid: { display: true, color: theme.grid },
    border: { color: theme.grid },
    beginAtZero: true,
    ticks: {
      color: theme.tick,
      callback: (value: string | number) =>
        typeof value === 'number' ? formatNumber(value, valueFormat) : value,
    },
  };

  const categoryAxis = {
    // Sin rejilla y SIN `beginAtZero`: no es un eje numérico.
    grid: { display: false, color: theme.grid },
    border: { color: theme.grid },
    ticks: { color: theme.tick },
  };

  return horizontal
    ? { x: valueAxis, y: categoryAxis }
    : { x: categoryAxis, y: valueAxis };
}
